import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  Users,
  CalendarDays,
  Receipt,
  BarChart3,
  AlertTriangle,
  FileText,
  Bot,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { pageTransition } from "@/lib/utils";
import { getAIStatus, type AIStatus } from "@/api/ai";
import { PageHeader } from "@/components/layout/PageHeader";
import { CopilotChat } from "@/components/ai/CopilotChat";

// Copilot tile metadata — shown on the left sidebar.
interface CopilotTile {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
}

const COPILOT_TILES: CopilotTile[] = [
  {
    id: "general",
    label: "General",
    description: "Ask anything",
    icon: Sparkles,
  },
  {
    id: "employees",
    label: "People",
    description: "Directory & profiles",
    icon: Users,
  },
  {
    id: "leave",
    label: "Leave",
    description: "Balances & requests",
    icon: CalendarDays,
  },
  {
    id: "invoices",
    label: "Invoicing",
    description: "Status & balances",
    icon: Receipt,
  },
  {
    id: "reports",
    label: "Reports",
    description: "KPIs & analytics",
    icon: BarChart3,
  },
  {
    id: "complaints",
    label: "Complaints",
    description: "Case load & trends",
    icon: AlertTriangle,
  },
  {
    id: "documents",
    label: "Docs",
    description: "Policy search",
    icon: FileText,
  },
];

export function AIAssistantPage() {
  const [status, setStatus] = useState<AIStatus | null>(null);
  const [copilot, setCopilot] = useState<string>("general");

  useEffect(() => {
    (async () => {
      try {
        setStatus(await getAIStatus());
      } catch {
        // ignore — chat still works, just without status hint
      }
    })();
  }, []);

  const availableTiles = useMemo(() => {
    if (!status?.copilots) return COPILOT_TILES;
    const set = new Set(status.copilots);
    return COPILOT_TILES.filter((t) => set.has(t.id));
  }, [status]);

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="AI Assistant"
        subtitle={
          status
            ? status.configured
              ? `Powered by ${status.model} · retrieval: ${status.embedding_backend || "n/a"}`
              : "Running in offline mode — set ANTHROPIC_API_KEY to enable chat"
            : "Loading…"
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4">
        {/* Copilot picker */}
        <nav aria-label="Copilots" className="space-y-1">
          {availableTiles.map((tile) => {
            const Icon = tile.icon;
            const active = copilot === tile.id;
            return (
              <button
                key={tile.id}
                onClick={() => setCopilot(tile.id)}
                className={`w-full text-left flex items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
                  active
                    ? "border-accent bg-accent/10 text-text-primary"
                    : "border-border bg-surface text-text-primary hover:border-accent/60 hover:bg-accent/5"
                }`}
              >
                <div
                  className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md ${
                    active ? "bg-accent/20" : "bg-background"
                  }`}
                >
                  <Icon className={`h-4 w-4 ${active ? "text-accent" : "text-text-muted"}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{tile.label}</p>
                  <p className="text-[11px] text-text-muted truncate">
                    {tile.description}
                  </p>
                </div>
              </button>
            );
          })}
          <div className="mt-4 rounded-lg border border-dashed border-border bg-surface px-3 py-3 text-[11px] text-text-muted flex gap-2">
            <Bot className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
            <span>
              Each copilot can call a scoped set of server-side tools. The list
              of tools it used appears under every reply.
            </span>
          </div>
        </nav>

        {/* Active chat */}
        <CopilotChat copilot={copilot} />
      </div>
    </motion.div>
  );
}
